import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogBody,
  DialogHeader,
  Icon,
  Card,
  Button,
  Input,
  FileUpload,
  Textarea,
  Skeleton,
} from '@repo/ui';
import { z, type ZodIssue } from 'zod';
import { get, isEmpty, size } from 'lodash';

import { numberUtil } from '@xtnd/web/shared';
import {
  FileCategory,
  MatterInvoiceSummaryFile,
  UpdateAppealStatusPayload,
} from '../../types/index.js';
import { AppealStatus } from '@repo/types';
import { useGetInvoice } from '../../queries/invoiceQuery.js';
import { AppealSummary, AppealSummarySkeleton } from '../appeals/AppealSummary.js';
import {
  useMatterInvoiceSummaryFilesQuery,
  useUploadMatterInvoiceSummaryFiles,
} from '../../queries/index.js';
import { ExistingBARFilesSection } from './FileAttachments.js';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../constants/attachments.js';

interface MarkPartialWriteOffModalProps {
  isOpen: boolean;
  invoiceId: string;
  invoiceNumber: string;
  isLoading: boolean;
  disabled?: boolean;
  targetStatus?: AppealStatus;
  setLoading: (loading: { discountOnly: boolean }) => void;
  onSubmit: (payload: UpdateAppealStatusPayload) => Promise<void>;
  onClose: (success?: boolean) => void;
}

interface DiscountFormData {
  feesCut: string;
  expensesCut: string;
  promptPayment: string;
  bulkDiscount: string;
  adminFee: string;
  comment: string;
  files: File[];
}

const initialFormData: DiscountFormData = {
  feesCut: '0',
  expensesCut: '0',
  promptPayment: '0',
  bulkDiscount: '0',
  adminFee: '0',
  comment: '',
  files: [],
};

const FIELD_LABEL = {
  feesCut: 'Fees Cut',
  expensesCut: 'Expenses Cut',
  promptPayment: 'Prompt Payment',
  bulkDiscount: 'Bulk Discount',
  adminFee: 'Administrative Fee',
};

const numericSchemaAllowZero = (fieldLabel: string) =>
  z
    .string({ required_error: `${fieldLabel} is required` })
    .trim()
    .transform((value: string) => Number(value))
    .refine((value: number) => !Number.isNaN(value), {
      message: `${fieldLabel} must be a number`,
    })
    .refine((value: number) => value >= 0, {
      message: `${fieldLabel} must be greater than or equal to 0`,
    });

const discountSchema = z.object({
  feesCut: numericSchemaAllowZero('Fees cut'),
  expensesCut: numericSchemaAllowZero('Expenses cut'),
  promptPayment: numericSchemaAllowZero('Prompt payment'),
  bulkDiscount: numericSchemaAllowZero('Bulk discount'),
  adminFee: numericSchemaAllowZero('Administrative fee'),
  comment: z.string().optional(),
});

/**
 * Skeleton component for loading state when invoice data is being fetched
 */
const MarkPartialWriteOffSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col lg:flex-row gap-6 pt-4">
      {/* LEFT COLUMN: Form Skeleton */}
      <div className="flex-1 space-y-6">
        {/* Financial Information Section Skeleton */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Skeleton className="w-10 h-10 rounded-lg mr-4" />
              <div>
                <Skeleton className="h-6 w-48" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column: Input Fields Skeleton */}
            <div className="flex flex-col gap-4">
              {/* Appeal Categories Skeleton */}
              <Skeleton className="h-3 w-32 mb-2" />
              <div className="space-y-4">
                <div>
                  <Skeleton className="h-3 w-20 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div>
                  <Skeleton className="h-3 w-24 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>

              {/* Write-offs Section Skeleton */}
              <div className="space-y-4 pt-2">
                <Skeleton className="h-3 w-24 mb-2" />
                <div className="flex flex-col gap-4">
                  <div>
                    <Skeleton className="h-3 w-28 mb-2" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div>
                    <Skeleton className="h-3 w-28 mb-2" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div>
                    <Skeleton className="h-3 w-20 mb-2" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Summary Cards Skeleton */}
            <div className="flex flex-col justify-start space-y-6">
              {/* Amount to Be Appealed Card Skeleton */}
              <Card className="rounded-xl p-5 shadow-sm">
                <div className="flex items-center mb-1">
                  <Skeleton className="w-3.5 h-3.5 rounded mr-2" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <Skeleton className="h-10 w-32 mt-2 mb-2" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6 mt-1" />
              </Card>

              {/* Agreed to Pay Card Skeleton */}
              <Card className="rounded-xl p-5 shadow-sm">
                <div className="flex items-center mb-1">
                  <Skeleton className="w-3.5 h-3.5 rounded mr-2" />
                  <Skeleton className="h-3 w-36" />
                </div>
                <Skeleton className="h-10 w-32 mt-2 mb-2" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5 mt-1" />
              </Card>

              {/* Logic Summary Card Skeleton */}
              <Card className="rounded-xl p-5">
                <div className="flex items-center mb-2">
                  <Skeleton className="w-4 h-4 rounded mr-2" />
                  <Skeleton className="h-4 w-28" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <div className="pt-2">
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Card>

        {/* Submission Details Section Skeleton */}
        <Card className="p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <Skeleton className="w-10 h-10 rounded-lg mr-4" />
            <Skeleton className="h-6 w-40" />
          </div>
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-24 w-full mb-4" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-40 mb-1" />
            <Skeleton className="h-3 w-64 mb-2" />
            <Skeleton className="h-32 w-full rounded-lg border-2 min-h-[10rem] border-dashed" />
            <div className="flex gap-2 mt-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
        </Card>
      </div>

      {/* RIGHT COLUMN: Summary Skeleton */}
      <div className="lg:w-80 space-y-6">
        <AppealSummarySkeleton />

        {/* Final Action Card Skeleton */}
        <Card className="p-6 rounded-2xl shadow-xl sticky top-24">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-4 w-20" />
            </div>

            <div className="p-3 rounded-xl border">
              <div className="flex justify-between items-center mb-1">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-6 w-24" />
              </div>
              <Skeleton className="h-1.5 w-full rounded-full my-2" />
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg">
            <div className="flex gap-3">
              <Skeleton className="w-5 h-5 rounded-full shrink-0" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </div>
            </div>
          </div>

          <Skeleton className="h-10 w-full rounded-lg mt-6" />
        </Card>
      </div>
    </div>
  );
};

/**
 * Modal component for marking an invoice as "Discount Only" (Waived).
 * This bypasses the appeal workflow and applies only standard discounts.
 */
export const MarkPartialWriteOffModal: React.FC<MarkPartialWriteOffModalProps> = ({
  isOpen,
  invoiceId,
  invoiceNumber,
  isLoading,
  onSubmit,
  setLoading,
  onClose,
  disabled,
  targetStatus = AppealStatus.NO_APPEAL,
}) => {
  const { data: invoice, isLoading: isInvoiceLoading } = useGetInvoice(invoiceId, {
    enabled: isOpen && !isEmpty(invoiceId),
  });

  const { mutateAsync: uploadFiles } = useUploadMatterInvoiceSummaryFiles(invoiceId, 'AR Appeal', {
    isShowToast: false,
    category: FileCategory.PAYMENT_SUPPORT,
  });

  const {
    data: response,
    isLoading: isLoadingGetFiles,
  } = useMatterInvoiceSummaryFilesQuery(invoiceId, { enabled: isOpen });
  const existingFiles = response?.data || [];

  const actualBalance = get(invoice, 'actualBalance', 0);

  const [formData, setFormData] = useState<DiscountFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof DiscountFormData, string>>>({});
  const [selectedExistingFiles, setSelectedExistingFiles] = useState<MatterInvoiceSummaryFile[]>(
    []
  );

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData(initialFormData);
      setErrors({});
      setSelectedExistingFiles([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (invoice && isOpen) {
      setFormData(prev => ({
        ...prev,
        feesCut: get(invoice, 'appeal.feesCut', 0).toFixed(2),
        expensesCut: get(invoice, 'appeal.expensesCut', 0).toFixed(2),
      }));
    }
  }, [invoice, isOpen]);

  const isFormValid = useMemo(() => {
    const hasError = Object.values(errors ?? {}).some(error => Boolean(error));
    if (hasError) {
      return false;
    }

    return true;
  }, [errors]);

  // Calculate total appeal amount (feesCut + expensesCut)
  const totalAppeal = useMemo(() => {
    const feesCut = Number(formData.feesCut || 0);
    const expensesCut = Number(formData.expensesCut || 0);
    let totalAppeal = feesCut + expensesCut;
    if (totalAppeal < 0 || totalAppeal > actualBalance) {
      totalAppeal = 0;
    }
    return totalAppeal;
  }, [formData.feesCut, formData.expensesCut, actualBalance]);

  // Calculate other adjustments (write-offs)
  const otherAdjustments = useMemo(() => {
    const promptPayment = Number(formData.promptPayment || 0);
    const bulkDiscount = Number(formData.bulkDiscount || 0);
    const adminFee = Number(formData.adminFee || 0);
    return promptPayment + bulkDiscount + adminFee;
  }, [formData.promptPayment, formData.bulkDiscount, formData.adminFee]);

  // Calculate agreed to pay
  const agreedToPay = useMemo(() => {
    let agreedToPay = actualBalance - totalAppeal - otherAdjustments;
    if (agreedToPay < 0) {
      agreedToPay = 0;
    }
    return agreedToPay;
  }, [totalAppeal, actualBalance, otherAdjustments]);

  const handleNumberChange = (field: keyof DiscountFormData, value: string) => {
    const normalized = numberUtil.normalizeNumericString(value);
    const numericValue = Number(normalized);
    const AMOUNT_REGEX = /^\d*\.?\d{0,2}$/;

    if (!AMOUNT_REGEX.test(normalized)) {
      return;
    }

    setFormData(prev => ({ ...prev, [field]: normalized }));
    setErrors(prev => ({ ...prev, [field]: '' }));

    if (!normalized) {
      setErrors(prev => ({
        ...prev,
        [field]: `${FIELD_LABEL[field as keyof typeof FIELD_LABEL]} is required`,
      }));
      return;
    }

    // Validate total doesn't exceed outstanding amount
    const feesCut = field === 'feesCut' ? numericValue : Number(formData.feesCut || 0);
    const expensesCut = field === 'expensesCut' ? numericValue : Number(formData.expensesCut || 0);
    const promptPayment =
      field === 'promptPayment' ? numericValue : Number(formData.promptPayment || 0);
    const bulkDiscount =
      field === 'bulkDiscount' ? numericValue : Number(formData.bulkDiscount || 0);
    const adminFee = field === 'adminFee' ? numericValue : Number(formData.adminFee || 0);

    const totalAppeal = feesCut + expensesCut;
    const totalAdjustments = Number((totalAppeal + promptPayment + bulkDiscount + adminFee).toFixed(2));

    if (totalAdjustments > actualBalance) {
      setErrors(prev => ({
        ...prev,
        [field]: `The total write-offs and the amount to be write-off must be less than or equal to ${numberUtil.formatCurrency(actualBalance)}`,
      }));
    }
  };

  const handleInputChange = (field: keyof DiscountFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    try {
      setLoading({ discountOnly: true });

      // Validate form with Zod
      const result = discountSchema.safeParse(formData);

      if (!result.success) {
        const zodErrors: Partial<Record<keyof DiscountFormData, string>> = {};

        result.error.issues.forEach((issue: ZodIssue) => {
          const field = issue.path[0] as keyof DiscountFormData | undefined;
          if (field && !zodErrors[field]) {
            zodErrors[field] = issue.message;
          }
        });

        setErrors(prev => ({
          ...prev,
          ...zodErrors,
        }));
        return;
      }

      const hasError = Object.values(errors ?? {}).some(error => Boolean(error));
      if (hasError) {
        return;
      }

      const feesCut = Number(formData.feesCut || 0);
      const expensesCut = Number(formData.expensesCut || 0);
      const promptPayment = Number(formData.promptPayment || 0);
      const bulkDiscount = Number(formData.bulkDiscount || 0);
      const adminFee = Number(formData.adminFee || 0);

      let files = [] as MatterInvoiceSummaryFile[];

      // Include selected existing files (same behavior as To Be Appealed / No Appeal)
      if (size(selectedExistingFiles) > 0) {
        files = [...files, ...selectedExistingFiles];
      }

      if (size(formData.files) > 0) {
        const uploadResult = await uploadFiles({
          files: formData.files,
          successCallback: async response => {
            files = [
              ...files,
              ...(get(response, 'successFiles', []) as MatterInvoiceSummaryFile[]),
            ];
          },
        });

        if (uploadResult.errorFiles && uploadResult.errorFiles.length > 0) {
          return;
        }
      }

      const remainingAmount = Number(feesCut || 0) + Number(expensesCut || 0);

      const payload: UpdateAppealStatusPayload = {
        invoiceId,
        nextStatus: targetStatus,
        remainingAmount,
        feesCut,
        expensesCut,
        promptPayment,
        bulkDiscount,
        administrativeFee: adminFee,
        comment: formData.comment || undefined,
        files,
      };

      await onSubmit(payload);
    } finally {
      setLoading({ discountOnly: false });
    }
  };

  const handleClose = (success?: boolean) => {
    if (isLoading) {
      return;
    }

    setFormData(initialFormData);
    setErrors({});
    setSelectedExistingFiles([]);
    onClose(success ?? false);
  };

  const handleFilesChange = (files: File[]) => {
    setFormData(prev => ({ ...prev, files }));
  };

  const handleExistingFilesChange = (files: MatterInvoiceSummaryFile[]) => {
    setSelectedExistingFiles(files);
  };

  const invoiceSuffix = invoiceNumber ? ` - INV #${invoiceNumber}` : '';
  const modalTitle =
    targetStatus === AppealStatus.DEADLINE_EXPIRED
      ? `Deadline Expired${invoiceSuffix}`
      : `No Appeal${invoiceSuffix}`;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xxllg"
      className="!max-h-[90vh] overflow-y-auto"
      showCloseButton={false}
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            {modalTitle}
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={() => handleClose(false)}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="pt-0 space-y-6">
        {isInvoiceLoading || isLoadingGetFiles ? (
          <MarkPartialWriteOffSkeleton />
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 pt-4">
            {/* LEFT COLUMN: Form */}
            <div className="flex-1 space-y-6">
              {/* Financial Adjustments Card */}
              <Card className="p-6 opacity-0 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                      <Icon
                        name="HiCurrencyDollar"
                        className="w-5 h-5 text-xtnd-blue dark:text-xtnd-blue-300"
                      />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                        Financial Information
                      </h2>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left Column: Input Fields */}
                  <div className="flex flex-col gap-4">
                    {/* Appeal Categories */}
                    <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light-400 uppercase tracking-wider flex items-center">
                      Write-offs
                    </h4>
                    <div>
                      <label className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Fees Cut <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        value={formData.feesCut}
                        onChange={e => handleNumberChange('feesCut', e.target.value)}
                        error={errors.feesCut}
                        disabled={disabled}
                        required
                        fullWidth
                        placeholder="0.00"
                        className="dark:border-xtnd-dark-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Expenses Cut <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        value={formData.expensesCut}
                        onChange={e => handleNumberChange('expensesCut', e.target.value)}
                        error={errors.expensesCut}
                        disabled={disabled}
                        required
                        fullWidth
                        placeholder="0.00"
                        className="dark:border-xtnd-dark-600"
                      />
                    </div>

                    {/* Write-offs Section */}
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light-400 uppercase tracking-wider">
                          Discounts
                        </h4>
                      </div>

                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1">
                            Prompt Payment <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="number"
                            value={formData.promptPayment}
                            onChange={e => handleNumberChange('promptPayment', e.target.value)}
                            error={errors.promptPayment}
                            disabled={disabled}
                            fullWidth
                            placeholder="0.00"
                            className="dark:border-xtnd-dark-600"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1">
                            Bulk Discount <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="number"
                            value={formData.bulkDiscount}
                            onChange={e => handleNumberChange('bulkDiscount', e.target.value)}
                            error={errors.bulkDiscount}
                            disabled={disabled}
                            fullWidth
                            placeholder="0.00"
                            className="dark:border-xtnd-dark-600"
                          />
                        </div>
                        <div className="col-span-full">
                          <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1">
                            Admin Fee <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="number"
                            value={formData.adminFee}
                            onChange={e => handleNumberChange('adminFee', e.target.value)}
                            error={errors.adminFee}
                            disabled={disabled}
                            fullWidth
                            placeholder="0.00"
                            className="dark:border-xtnd-dark-600"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Summary Cards */}
                  <div className="flex flex-col justify-start space-y-6">
                    {/* Amount to Be Appealed Card */}
                    <Card className="rounded-xl p-5 shadow-sm">
                      <h4 className="text-xtnd-dark-600 dark:text-xtnd-light-400 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
                        <Icon name="HiCurrencyDollar" className="w-3.5 h-3.5 mr-2" />
                        Amount to Be Write-Off
                      </h4>
                      <p className="text-3xl font-mono font-bold text-xtnd-blue dark:text-xtnd-blue-300">
                        {numberUtil.formatCurrency(totalAppeal)}
                      </p>
                      <p className="mt-2 text-[11px] text-xtnd-dark-600 dark:text-xtnd-light-400 leading-relaxed italic">
                        Amount to Be Write-Off is automatically calculated based on Fees Cut and
                        Expenses Cut.
                      </p>
                    </Card>

                    {/* Agreed to Pay Card */}
                    <Card className="rounded-xl p-5 shadow-sm">
                      <h4 className="text-xtnd-dark-600 dark:text-xtnd-light-400 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
                        <Icon name="HiCreditCard" className="w-3.5 h-3.5 mr-2" />
                        Agreed to Pay (Est.)
                      </h4>
                      <p className="text-3xl font-mono font-bold text-xtnd-blue dark:text-xtnd-blue-300">
                        {numberUtil.formatCurrency(agreedToPay)}
                      </p>
                      <p className="mt-2 text-[11px] text-xtnd-dark-600 dark:text-xtnd-light-400 leading-relaxed italic">
                        Remaining balance client is expected to pay after all reductions.
                      </p>
                    </Card>

                    {/* Logic Summary Card */}
                    <Card className="!bg-amber-50 dark:!bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-xl p-5">
                      <h4 className="text-amber-800 dark:text-amber-300 text-sm font-bold mb-2 flex items-center">
                        <Icon name="HiInformationCircle" className="w-4 h-4 mr-2" />
                        Logic Summary
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-amber-600 dark:text-amber-400">
                            Amount to Be Write-Off (Fees+Exp):
                          </span>
                          <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                            {numberUtil.formatCurrency(totalAppeal)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-amber-600 dark:text-amber-400">Discounts:</span>
                          <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                            {numberUtil.formatCurrency(otherAdjustments)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs border-t border-amber-200 dark:border-amber-400 pt-2 mt-2">
                          <span className="text-amber-600 dark:text-amber-400">All Reductions</span>
                          <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                            {numberUtil.formatCurrency(totalAppeal + otherAdjustments)}
                          </span>
                        </div>
                        <div className="pt-2 mt-2">
                          <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed">
                            Agreed to Pay = Outstanding ({numberUtil.formatCurrency(actualBalance)})
                            - All Reductions (
                            {numberUtil.formatCurrency(totalAppeal + otherAdjustments)})
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </Card>

              {/* Submission Details Section */}
              <Card
                className="p-6 shadow-sm opacity-0 animate-fadeInUp"
                style={{ animationDelay: '0.2s' }}
              >
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                    <Icon
                      name="HiDocumentText"
                      className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
                    />
                  </div>
                  <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                    Submission Details
                  </h2>
                </div>
                <label className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2">
                  Comment (Optional)
                </label>
                <Textarea
                  disabled={disabled}
                  value={formData.comment}
                  onChange={e => handleInputChange('comment', e.target.value)}
                  rows={4}
                  fullWidth
                  placeholder="Comment for the discount only..."
                />

                <div className="mt-4 space-y-4">
                  <div className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                    <p>Attachments (Optional)</p>
                    <span className="text-xs text-gray-400 dark:text-gray-200 italic font-normal">
                      Please include the original BAR and the appeal BAR.
                    </span>
                  </div>
                  <FileUpload
                    disabled={disabled}
                    files={formData.files}
                    isMultiple={true}
                    accept={AR_ATTACHMENT_ACCEPT}
                    maxSize={AR_ATTACHMENT_MAX_SIZE}
                    onChange={handleFilesChange}
                    isDisplayUploadButton={false}
                    maxFileNumber={12}
                    error={errors.files}
                    setError={(error: string) => setErrors(prev => ({ ...prev, files: error }))}
                  />
                </div>
                <div className="mt-6">
                  <ExistingBARFilesSection
                    existingFiles={existingFiles}
                    onSelectedFilesChange={handleExistingFilesChange}
                    columns={1}
                  />
                </div>
              </Card>
            </div>

            {/* RIGHT COLUMN: Summary */}
            <div
              className="lg:w-80 space-y-6 opacity-0 animate-fade-in-right"
              style={{ animationDelay: '0.2s' }}
            >
              {invoice && (
                <AppealSummary
                  basicInformation={{
                    invoiceNumber: invoice.invoiceNumber,
                    invoiceDate: invoice.invoiceDate,
                    clientName: invoice.clientName || '',
                    matterDisplayNumber: invoice.matterDisplayNumber,
                    totalAmount: invoice.total,
                    amountPaid: invoice.paid,
                    amountDue: invoice.outstandingBalance,
                    paid: invoice.paid,
                    creditsIssued: invoice.creditsIssued,
                    responsibleAttorneyName: invoice.responsibleAttorneyName,
                  }}
                />
              )}

              <Card
                className="!bg-transparent text-xtnd-dark dark:text-xtnd-light p-6 rounded-2xl shadow-xl sticky top-24 opacity-0 animate-fadeInRight"
                style={{ animationDelay: '0.15s' }}
              >
                <div className="space-y-4">
                  <div className="flex justify-between items-center font-bold text-xtnd-dark dark:text-xtnd-light">
                    <span className="text-xs">Amount to Be Write-Off</span>
                    <span className=" font-mono">{numberUtil.formatCurrency(totalAppeal)}</span>
                  </div>

                  <div className="p-3 bg-xtnd-blue/50 rounded-xl border border-xtnd-blue/80 shadow-sm">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[0.625rem] shadow-sm text-xtnd-dark dark:text-xtnd-light uppercase font-bold">
                        Agreed to Pay
                      </span>
                      <span className="text-xtnd-dark dark:text-xtnd-light font-mono font-bold text-lg">
                        {numberUtil.formatCurrency(agreedToPay)}
                      </span>
                    </div>
                    <div className="w-full bg-xtnd-dark-700 h-1.5 rounded-full overflow-hidden my-2">
                      <div
                        className="bg-xtnd-blue dark:bg-xtnd-light h-full transition-all duration-500"
                        style={{
                          width:
                            actualBalance > 0
                              ? `${Math.min(100, Math.max(0, (agreedToPay / actualBalance) * 100))}%`
                              : '0%',
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  loading={isLoading}
                  disabled={isLoading || !isFormValid}
                  variant="primary"
                  className="w-full  mt-6 py-2 font-bold flex items-center justify-center gap-3"
                >
                  Confirm
                </Button>
              </Card>
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
};

export default MarkPartialWriteOffModal;
